import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Search,
  Send,
  Users,
} from "lucide-react";
import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { getApiErrorMessage, portalApi } from "../api/client";
import type { ApiEnvelope } from "../api/types";
import { BulkUploadModal } from "../compliance/BulkUploadModal";
import { PageError } from "../components/PageError";
import type {
  AssetOverview,
  AssetOverviewItem,
  AssetRequirementSummary,
} from "../types/compliance";
import { Badge, Button, Input, Label } from "../components/ui";

type PageState =
  | { status: "loading" }
  | { status: "forbidden" }
  | { status: "error"; message: string }
  | { status: "ready"; data: AssetOverview };

export function AssetsPage() {
  const { cdassettype = "" } = useParams();
  const location = useLocation();
  const [state, setState] = React.useState<PageState>({ status: "loading" });
  const [search, setSearch] = React.useState("");
  const [onlyIncomplete, setOnlyIncomplete] = React.useState(false);
  const [openAsset, setOpenAsset] = React.useState<number | null>(null);
  const [bulkRequirement, setBulkRequirement] =
    React.useState<AssetRequirementSummary | null>(null);
  const sent = new URLSearchParams(location.search).get("sent");

  const load = React.useCallback(async () => {
    setState({ status: "loading" });
    try {
      const response = await portalApi.get<ApiEnvelope<AssetOverview>>(
        `/portal/requirements/asset-type/${Number(cdassettype)}/overview`
      );
      setState({ status: "ready", data: response.data.data });
    } catch (requestError: any) {
      if (requestError.response?.status === 403) {
        setState({ status: "forbidden" });
      } else {
        setState({
          status: "error",
          message: getApiErrorMessage(
            requestError,
            "Não foi possível carregar os ativos."
          ),
        });
      }
    }
  }, [cdassettype]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="space-y-5">
        <div className="h-20 w-80 animate-pulse rounded-xl bg-slate-200" />
        <div className="h-48 animate-pulse rounded-2xl bg-slate-200" />
        <div className="h-64 animate-pulse rounded-2xl bg-slate-200" />
      </div>
    );
  }
  if (state.status === "forbidden") {
    return (
      <PageError
        title="Acesso não permitido"
        description="Seu perfil não pode visualizar estes ativos."
      />
    );
  }
  if (state.status === "error") {
    return (
      <PageError
        title="Não foi possível carregar"
        description={state.message}
        retry={() => void load()}
      />
    );
  }

  const { data } = state;
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleAssets = data.assets.filter((asset) => {
    if (onlyIncomplete && asset.percent === 100) return false;
    if (!normalizedSearch) return true;
    return [asset.nmasset, asset.idasset, asset.nrdocument]
      .filter(Boolean)
      .some((value) =>
        String(value).toLocaleLowerCase().includes(normalizedSearch)
      );
  });

  return (
    <div className="space-y-6">
      <header className="flex items-start gap-3">
        <Button asChild variant="outline" size="icon" className="mt-0.5 bg-white">
          <Link to="/documentation" aria-label="Voltar para documentação">
            <ArrowLeft size={18} />
          </Link>
        </Button>
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <Users size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
            {data.nmassettype}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {data.summary.total} ativos · {data.summary.complete} com
            documentação obrigatória completa
          </p>
        </div>
      </header>

      {sent && (
        <div className="flex items-start gap-3 rounded-xl border border-teal-200 bg-teal-50 p-4 text-sm text-teal-800">
          <CheckCircle2 className="mt-0.5 shrink-0" size={18} />
          <p>{sent} arquivo(s) foram enviados e estão em análise.</p>
        </div>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <h2 className="font-semibold text-slate-900">Envie por documento</h2>
          <p className="mt-1 text-sm text-slate-600">
            Cada card envia o mesmo requerimento para vários ativos de uma vez.
          </p>
        </div>
        {data.requirements.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-5 text-sm text-slate-600">
            Nenhum requerimento foi configurado para este tipo de ativo.
          </p>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {data.requirements.map((requirement) => (
              <Button
                key={requirement.cdtyperequeriment}
                type="button"
                variant="outline"
                className={`h-auto w-full flex-col items-stretch rounded-xl p-4 text-left hover:border-primary ${
                  requirement.fgrequire === 1
                    ? "border-slate-200 bg-white"
                    : "border-dashed border-slate-300 bg-slate-50"
                }`}
                onClick={() => setBulkRequirement(requirement)}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p
                      className={`truncate text-sm font-medium ${
                        requirement.fgrequire === 1
                          ? "text-slate-900"
                          : "text-slate-600"
                      }`}
                    >
                      {requirement.sgrequirement || requirement.nmrequirement}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {requirement.nmrequirement}
                    </p>
                  </div>
                  {requirement.fgrequire === 2 ? (
                    <Badge
                      variant="outline"
                      className="rounded-full border-dashed border-slate-300 bg-slate-50 text-slate-500"
                    >
                      Complementar
                    </Badge>
                  ) : requirement.missing > 0 ? (
                    <Badge
                      variant="outline"
                      className={`rounded-full ${
                        requirement.worstseverity === 3
                          ? "border-red-200 bg-red-50 text-red-700"
                          : "border-amber-200 bg-amber-50 text-amber-800"
                      }`}
                    >
                      {requirement.missing} faltam
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="rounded-full border-teal-200 bg-teal-50 text-teal-700"
                    >
                      Completo
                    </Badge>
                  )}
                </div>
                <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${
                      requirement.fgrequire === 2
                        ? "bg-slate-400"
                        : requirement.worstseverity === 3
                        ? "bg-red-500"
                        : requirement.missing > 0
                        ? "bg-amber-500"
                        : "bg-teal-600"
                    }`}
                    style={{ width: `${requirement.percent}%` }}
                  />
                </div>
                <div className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-500">
                  <span>
                    {requirement.met}/{requirement.total}
                    {requirement.fgrequire === 2
                      ? " · não é exigido"
                      : requirement.missing === 0
                      ? " · completo"
                      : ""}
                  </span>
                  <span className="inline-flex items-center gap-1 font-medium text-teal-700">
                    <Send size={13} />
                    Envio em massa
                  </span>
                </div>
              </Button>
            ))}
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap gap-3 border-b border-slate-200 p-4">
          <Label className="relative min-w-52 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-3 text-slate-400"
              size={17}
            />
            <Input
              type="search"
              className="h-11 bg-white pl-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome, código ou documento"
            />
          </Label>
          <Button
            type="button"
            variant={onlyIncomplete ? "secondary" : "outline"}
            size="lg"
            onClick={() => setOnlyIncomplete((value) => !value)}
          >
            Só incompletos
          </Button>
        </div>

        {visibleAssets.length === 0 ? (
          <div className="p-8 text-center">
            <Search className="mx-auto text-slate-400" size={25} />
            <h2 className="mt-3 font-semibold text-slate-900">
              {data.assets.length === 0
                ? "Nenhum ativo vinculado"
                : "Nenhum ativo encontrado"}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {data.assets.length === 0
                ? "Quando a empresa compradora vincular ativos deste tipo, eles aparecerão aqui."
                : "Ajuste a busca ou desative o filtro de incompletos."}
            </p>
          </div>
        ) : (
          visibleAssets.map((asset) => (
            <AssetRow
              key={asset.cdasset}
              asset={asset}
              open={openAsset === asset.cdasset}
              onToggle={() =>
                setOpenAsset((current) =>
                  current === asset.cdasset ? null : asset.cdasset
                )
              }
            />
          ))
        )}
      </section>

      {bulkRequirement && (
        <BulkUploadModal
          open
          cdassettype={data.cdassettype}
          nmassettype={data.nmassettype}
          requirement={bulkRequirement}
          onClose={() => setBulkRequirement(null)}
        />
      )}
    </div>
  );
}

function AssetRow({
  asset,
  open,
  onToggle,
}: {
  asset: AssetOverviewItem;
  open: boolean;
  onToggle(): void;
}) {
  const initials = asset.nmasset
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
  return (
    <article className="border-b border-slate-200 last:border-b-0">
      <Button
        type="button"
        variant="ghost"
        className="h-auto w-full justify-start rounded-none p-4 text-left hover:bg-slate-50"
        onClick={onToggle}
      >
        <span
          className={`flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            asset.percent === 100
              ? "bg-teal-50 text-teal-700"
              : "bg-slate-100 text-slate-600"
          }`}
        >
          {initials || "AT"}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">
            {asset.nmasset}
          </p>
          <p className="truncate text-xs text-slate-500">
            {asset.idasset}
            {asset.nrdocument ? ` · ${asset.nrdocument}` : ""}
          </p>
        </div>
        <div className="hidden max-w-sm flex-wrap justify-end gap-1.5 md:flex">
          {asset.missing.slice(0, 3).map((missing) => (
            <Badge
              variant="outline"
              key={missing.cdreq}
              className={`rounded-full ${
                missing.fgseverity === 3
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-amber-200 bg-amber-50 text-amber-800"
              }`}
            >
              {missing.sgrequirement || missing.nmrequirement}
            </Badge>
          ))}
        </div>
        <span className="text-xs text-slate-500">
          {asset.required.met}/{asset.required.total}
        </span>
        {open ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
      </Button>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
          {asset.missing.length === 0 ? (
            <p className="text-sm text-teal-700">
              Todos os documentos obrigatórios estão atendidos.
            </p>
          ) : (
            <>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                O que falta
              </p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {asset.missing.map((missing) => (
                  <li
                    key={missing.cdreq}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                  >
                    {missing.nmrequirement}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </article>
  );
}
